import React, { useState } from 'react';
import { Badge, Group, Stack, Text, Code, ActionIcon, Tooltip, Modal, Alert, Button } from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconChevronUp, IconPlayerPlayFilled, IconPlayerPauseFilled, IconPlayerTrackNext, IconX } from '@tabler/icons-react';

/**
 * Surfaces `metadata.preconditions` on a plan card. Two visual modes:
 *
 *   variant="badge"  (default) — a small "⚠️ Setup required (N)" badge that
 *                                expands to a detail list on click. Compact —
 *                                use on result-list / library cards.
 *   variant="inline"          — always-rendered detail block (label + value +
 *                                action). Use on the Verify Plans review
 *                                screen and inside the pre-run modal.
 *
 * The plan-schema contract (backend/packages/test-plan/src/schema.ts):
 *   preconditions: Array<{ label: string, value: string, action?: string }>
 *
 * If the field is missing or empty, the component renders null — safe to drop
 * onto every plan card without first checking.
 */
function PreconditionsBadge({ preconditions, variant = 'badge', color = 'orange' }) {
  const [expanded, setExpanded] = useState(false);
  const list = Array.isArray(preconditions) ? preconditions.filter((p) => p && p.label && p.value) : [];

  if (list.length === 0) return null;

  if (variant === 'inline') {
    return (
      <Stack gap={4} mt={4}>
        {list.map((p, i) => (
          <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
            <IconAlertTriangle size={14} style={{ marginTop: 3, color: 'var(--mantine-color-orange-7)' }} />
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600}>
                {p.label}: <Code>{p.value}</Code>
              </Text>
              {p.action && (
                <Text size="xs" c="dimmed">
                  {p.action}
                </Text>
              )}
            </Stack>
          </Group>
        ))}
      </Stack>
    );
  }

  // Default: collapsed badge.
  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Group gap={4} wrap="nowrap">
        <Tooltip label={expanded ? 'Hide setup steps' : 'Show setup steps'}>
          <Badge
            size="xs"
            variant="filled"
            color={color}
            leftSection={<IconAlertTriangle size={11} />}
            style={{ cursor: 'pointer' }}
            onClick={() => setExpanded((v) => !v)}
          >
            Setup required ({list.length})
          </Badge>
        </Tooltip>
        <ActionIcon
          size="xs"
          variant="subtle"
          color="gray"
          onClick={() => setExpanded((v) => !v)}
          aria-label="toggle setup details"
        >
          {expanded ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
        </ActionIcon>
      </Group>
      {expanded && (
        <Stack gap={2} mt={2} pl={6}>
          {list.map((p, i) => (
            <Text key={i} size="xs">
              <strong>{p.label}:</strong> <Code style={{ fontSize: 11 }}>{p.value}</Code>
              {p.action ? ` — ${p.action}` : ''}
            </Text>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Tiny helper for callers that only want to know if the badge would render at
 * all (e.g., deciding whether to intercept Run with the runtime gate).
 */
export function hasPreconditions(plan) {
  const list = plan?.metadata?.preconditions;
  return Array.isArray(list) && list.some((p) => p && p.label && p.value);
}

/**
 * Runtime precondition gate — pauses a batch (or a single run) right before
 * the next dial when the upcoming plan needs manual IVR setup. The modal
 * names the plan it's gating, lists the switches the engineer must flip,
 * and offers Resume / Skip plan / Cancel batch. For a single-plan run the
 * Skip button is hidden (skip is the same as cancel).
 *
 * Wired from runBatchSequentially via `onPreconditionChange`: the runner
 * suspends the loop until `onResume` / `onSkip` / `onCancel` resolves the
 * decision promise. Auto-grouping (reorderForPreconditionGrouping) means
 * a 12-plan holiday batch only gates the engineer ONCE — once flipped, the
 * remaining plans share the same signature and dial through without
 * re-prompting.
 *
 * Props:
 *   opened   — boolean
 *   plan     — the v1 TestPlan about to dial (preconditions read off
 *              `metadata.preconditions`)
 *   position — { index, total } — drives "Plan 3 of 12" copy + whether the
 *              Skip button renders
 *   onResume — resolve { proceed: true } and dial now
 *   onSkip   — resolve { skip: true } (record SKIPPED, continue batch).
 *              Hidden when position.total === 1.
 *   onCancel — resolve { cancel: true } (stop the batch). Also fires on
 *              Escape / overlay click.
 */
export function RuntimePreconditionGateModal({ opened, plan, position, onResume, onSkip, onCancel }) {
  const list = Array.isArray(plan?.metadata?.preconditions)
    ? plan.metadata.preconditions.filter((p) => p && p.label && p.value)
    : [];

  // Defensive: a caller may pass a plan with no preconditions if the host
  // forgot to filter. Auto-resume so we don't strand the engineer behind
  // an empty modal.
  if (opened && plan && list.length === 0) {
    setTimeout(() => onResume?.(), 0);
    return null;
  }
  if (!opened || !plan || list.length === 0) return null;

  const total = position?.total ?? 1;
  const index = position?.index ?? 0;
  const isBatch = total > 1;
  const planName = plan?.metadata?.name || plan?.metadata?.id || 'Next plan';

  return (
    <Modal
      opened={opened}
      onClose={() => onCancel?.()}
      withCloseButton={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      centered
      size="lg"
      title={
        <Group gap="sm">
          <IconPlayerPauseFilled size={18} style={{ color: 'var(--mantine-color-orange-7)' }} />
          <Text fw={600}>{isBatch ? `Batch paused — set up before plan ${index + 1} of ${total}` : 'Set up the IVR before this run'}</Text>
        </Group>
      }
    >
      <Stack gap="md">
        <Alert color="orange" variant="light">
          <Text fw={600} size="sm">{planName}</Text>
          <Text size="xs" mt={4}>
            Flip the {list.length === 1 ? 'switch' : `${list.length} switches`} below on the destination IVR, then click <strong>Resume</strong>.
            {isBatch && ' Plans that share the same setup will continue automatically without re-prompting.'}
          </Text>
        </Alert>

        <Stack gap="xs">
          {list.map((p, i) => (
            <Group key={i} gap="sm" wrap="nowrap" align="flex-start">
              <IconAlertTriangle size={16} style={{ marginTop: 3, color: 'var(--mantine-color-orange-7)' }} />
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={600}>
                  {p.label}: <Code>{p.value}</Code>
                </Text>
                {p.action && (
                  <Text size="xs" c="dimmed">
                    {p.action}
                  </Text>
                )}
              </Stack>
            </Group>
          ))}
        </Stack>

        <Group justify="space-between" gap="sm">
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconX size={14} />}
            onClick={() => onCancel?.()}
          >
            {isBatch ? 'Cancel batch' : 'Cancel'}
          </Button>
          <Group gap="sm">
            {isBatch && onSkip && (
              <Button
                variant="default"
                leftSection={<IconPlayerTrackNext size={14} />}
                onClick={() => onSkip?.()}
              >
                Skip this plan
              </Button>
            )}
            <Button
              color="violet"
              leftSection={<IconPlayerPlayFilled size={14} />}
              onClick={() => onResume?.()}
            >
              Resume — dial now
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

export default PreconditionsBadge;
